// {{WORKTREE_ROOT}}/picosponsorconsole/app/utils/locale.ts
// PLAN2 WP10 — client-safe. useT() returns t(section, id, args) (the §5.18 signature). With sponsorship terms on
// (ps_terms=terms, RL-39) the id "<id>-terms" wins when it exists. {product} is filled from common.product-name on
// every message (§8). Missing id: a test fails (MissingLocaleId); elsewhere one warn per id and an empty string —
// never the id itself on screen (C-133). Dates passed as ICU arguments must be Date objects.
import { createContext, createElement, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { IntlMessageFormat } from "intl-messageformat";
import { PREF_COOKIES, prefCookieString, type TermsMode } from "./prefs";

export type LocaleStrings = Record<string, Record<string, string>>;
/** `reviewed` (R-175): false while the language is a machine draft no human has reviewed — the root then shows the
 * machine-translation note (WP33 fills the slot). en-US (the source) and en-XA (pseudo) are always reviewed. */
export interface LocaleBundle { lang: string; dir: "ltr" | "rtl"; strings: LocaleStrings; reviewed: boolean }
export type T = (section: string, id: string, args?: Record<string, unknown>) => string;

export class MissingLocaleId extends Error {
  constructor(public section: string, public id: string) { super(`missing locale id ${section}.${id}`); this.name = "MissingLocaleId"; }
}

export function resolveRaw(strings: LocaleStrings, section: string, id: string, terms: TermsMode): string | undefined {
  const sec = strings[section];
  if (!sec) return undefined;
  if (terms === "terms" && sec[`${id}-terms`] !== undefined) return sec[`${id}-terms`];
  return sec[id];
}

const warned = new Set<string>();
export function createT(bundle: LocaleBundle, terms: TermsMode, strict: boolean = process.env.NODE_ENV === "test"): T {
  const cache = new Map<string, IntlMessageFormat>();
  const product = bundle.strings.common?.["product-name"] ?? "";
  return (section, id, args) => {
    const raw = resolveRaw(bundle.strings, section, id, terms);
    if (raw === undefined) {
      if (strict) throw new MissingLocaleId(section, id);
      const key = `${section}.${id}`;
      if (!warned.has(key)) { warned.add(key); console.warn(`locale: missing id ${key}`); }
      return "";
    }
    if (!raw.includes("{")) return raw;
    const key = `${section} ${id} ${terms}`;
    let f = cache.get(key);
    if (!f) { f = new IntlMessageFormat(raw, bundle.lang); cache.set(key, f); }
    return String(f.format({ product, ...args }));
  };
}

interface LocaleContextValue { bundle: LocaleBundle; terms: TermsMode; setTerms: (m: TermsMode) => void; t: T }
const LocaleContext = createContext<LocaleContextValue | null>(null);

export function LocaleProvider({ bundle, terms: initial, children }: { bundle: LocaleBundle; terms: TermsMode; children: ReactNode }) {
  const [terms, setTermsState] = useState<TermsMode>(initial);
  const setTerms = useCallback((m: TermsMode) => {
    setTermsState(m);
    if (typeof document !== "undefined") document.cookie = prefCookieString(PREF_COOKIES.terms, m, window.location.protocol === "https:");
  }, []);
  const t = useMemo(() => createT(bundle, terms), [bundle, terms]);
  const value = useMemo(() => ({ bundle, terms, setTerms, t }), [bundle, terms, setTerms, t]);
  return createElement(LocaleContext.Provider, { value }, children);
}

function useLocaleContext(): LocaleContextValue {
  const c = useContext(LocaleContext);
  if (!c) throw new Error("locale: useT/useLocale used outside LocaleProvider");
  return c;
}
export function useT(): T { return useLocaleContext().t; }
export function useLocale() { const c = useLocaleContext(); return { lang: c.bundle.lang, dir: c.bundle.dir, terms: c.terms, setTerms: c.setTerms }; }

/** Test-only. */
export function __resetLocaleWarnings() { warned.clear(); }
